import { Theme } from "gatsby-interface"
import { Interpolation } from "@emotion/core"

// a bunch of new colors, loosely defined for now
// GATSBY_NEW is a slightly more vibrant purple
const GATSBY_NEW_110 = "#11081f"
const GATSBY_NEW_100 = "#1a0d31"
const GATSBY_NEW_70 = "#571d90"
const GATSBY_NEW = "#7026b9"
const GATSBY_NEW_BRIGHT = "#a456f0"
const GATSBY_NEW_BRIGHTER = "#e3c9fd"
// for INVERTED themes we need something darker than 90, which is
// where our current color scales end (ref. https://gatsby-interface.netlify.app/?path=/story/theme-colors--page)
const BLUE_100 = "#001d3d"
const MAGENTA_100 = "#3d0129"
const GREEN_100 = "#002400"
// for these colors our scales don't support _two_ steps that comply to the WCAG color contrast guidelines
// so we add a darker step for `hover` states
const ORANGE_100 = "#cc3b00"
const YELLOW_100 = "#8a6534"
const TEAL_100 = "#00756a"

export type ColorSchemeTheme = Theme & {
  colorScheme: {
    background?: string // section background color: white or GATSBY_NEW_110 for `INVERTED`
    heading?: string // section heading text color
    text?: string // section text color
    accent?: string // link color, a brighter shade of PRIMARY
    primary: string // primary theme color
    muted: string // faint color for backgrounds, borders, and accents that do not require high contrast with the background color – a very dark shade of primary, very close to background
    hover: string
    hoverDark?: string
    gradient: {
      start: string
      stop: string
      direction?: number
    }
  }
}

export const COLOR_SCHEMES = {
  Default: `DEFAULT`,
  Gatsby: `GATSBY`,
  Magenta: `MAGENTA`,
  Red: `RED`,
  Orange: `ORANGE`,
  Yellow: `YELLOW`,
  Green: `GREEN`,
  Teal: `TEAL`,
  Blue: `BLUE`,
  Neutral: `NEUTRAL`,
  GatsbyInverted: `GATSBY_INVERTED`,
  GatsbyConfInverted: `GATSBYCONF_INVERTED`,
  BlueInverted: `BLUE_INVERTED`,
  MagentaInverted: `MAGENTA_INVERTED`,
  GreenInverted: `GREEN_INVERTED`,
  NeutralInverted: `NEUTRAL_INVERTED`,
}

export type ColorSchemeCss = (theme: ColorSchemeTheme) => Interpolation

const colors = [
  `DEFAULT`,
  `GATSBY`,
  `MAGENTA`,
  `RED`,
  `ORANGE`,
  `YELLOW`,
  `GREEN`,
  `TEAL`,
  `BLUE`,
  `NEUTRAL`,
  `GATSBYCONF_INVERTED`,
  `GATSBY_INVERTED`,
  `BLUE_INVERTED`,
  `MAGENTA_INVERTED`,
  `GREEN_INVERTED`,
  `NEUTRAL_INVERTED`,
] as const

export type ColorSchemeOption = typeof colors[number]

export const isColorScheme = (
  color: ColorSchemeOption
): color is ColorSchemeOption => colors.includes(color)

const getColors = (interfaceTheme: Theme) => ({
  purple: interfaceTheme.colors.purple,
  whiteFade: interfaceTheme.colors.whiteFade,
  orange: interfaceTheme.colors.orange,
  magenta: interfaceTheme.colors.magenta,
  blue: interfaceTheme.colors.blue,
  green: interfaceTheme.colors.green,
  yellow: interfaceTheme.colors.yellow,
  teal: interfaceTheme.colors.teal,
  grey: interfaceTheme.colors.grey,
  red: interfaceTheme.colors.red,
  blackFade: interfaceTheme.colors.blackFade,
  shared: {
    heading: interfaceTheme.colors.grey[90],
    text: interfaceTheme.colors.grey[70],
    background: interfaceTheme.colors.white,
  },
  sharedInverted: {
    background: GATSBY_NEW_110,
    heading: interfaceTheme.colors.white,
    primary: interfaceTheme.colors.white,
    text: interfaceTheme.colors.whiteFade[80],
  },
})

export const colorSchemes = (interfaceTheme: Theme) => {
  const {
    purple,
    orange,
    magenta,
    blue,
    green,
    yellow,
    teal,
    grey,
    red,
    blackFade,
    shared,
    sharedInverted,
  } = getColors(interfaceTheme)

  return {
    DEFAULT: {
      ...shared,
      primary: purple[60],
      hover: purple[70],
      hoverDark: purple[70],
      muted: purple[5],
      accent: purple[50],
      gradient: {
        start: purple[50],
        stop: purple[60],
      },
    },
    GATSBY: {
      ...shared,
      primary: purple[60],
      hover: purple[70],
      hoverDark: purple[70],
      muted: purple[5],
      accent: purple[50],
      gradient: {
        start: purple[50],
        stop: purple[60],
      },
    },
    MAGENTA: {
      ...shared,
      primary: magenta[50],
      hover: magenta[60],
      hoverDark: magenta[70],
      muted: magenta[5],
      accent: magenta[50],
      gradient: {
        start: magenta[60],
        stop: magenta[70],
      },
    },
    BLUE: {
      ...shared,
      primary: blue[80],
      hover: blue[90],
      hoverDark: blue[90],
      muted: blue[5],
      accent: blue[50],
      gradient: {
        start: blue[60],
        stop: blue[70],
      },
    },
    GREEN: {
      ...shared,
      primary: green[80],
      hover: green[90],
      hoverDark: green[90],
      muted: green[5],
      accent: green[50],
      gradient: {
        start: green[60],
        stop: green[70],
      },
    },
    ORANGE: {
      ...shared,
      primary: ORANGE_100,
      hover: orange[90],
      hoverDark: purple[70],
      muted: orange[5],
      accent: orange[60],
      gradient: {
        start: orange[60],
        stop: orange[70],
      },
    },
    YELLOW: {
      ...shared,
      primary: yellow[90],
      hover: YELLOW_100,
      hoverDark: YELLOW_100,
      muted: yellow[5],
      accent: yellow[60],
      gradient: {
        start: yellow[60],
        stop: yellow[70],
      },
    },
    TEAL: {
      ...shared,
      primary: teal[90],
      hover: TEAL_100,
      hoverDark: TEAL_100,
      muted: teal[5],
      accent: teal[70],
      gradient: {
        start: teal[70],
        stop: teal[70],
      },
    },
    NEUTRAL: {
      ...shared,
      primary: grey[80],
      hover: grey[90],
      hoverDark: grey[90],
      muted: grey[5],
      accent: grey[60],
      gradient: {
        start: grey[50],
        stop: grey[60],
      },
    },
    RED: {
      ...shared,
      primary: red[70],
      hover: red[80],
      hoverDark: red[90],
      muted: red[5],
      accent: red[60],
      gradient: {
        start: red[60],
        stop: red[70],
      },
    },
    GATSBYCONF_INVERTED: {
      // Needed for gatsby conf 2022
      // TODO check if this can be removed now that we have gatsbyconf.com going again
      ...sharedInverted,
      hover: GATSBY_NEW_BRIGHTER,
      hoverDark: GATSBY_NEW_70,
      muted: GATSBY_NEW_100,
      primary: GATSBY_NEW,
      accent: GATSBY_NEW_BRIGHTER,
      gradient: {
        start: `#159BF3`,
        stop: `#FF0D6A`,
      },
    },
    GATSBY_INVERTED: {
      ...sharedInverted,
      hover: GATSBY_NEW_BRIGHTER,
      hoverDark: GATSBY_NEW_70,
      muted: GATSBY_NEW_100,
      primary: GATSBY_NEW,
      accent: GATSBY_NEW_BRIGHT,
      gradient: {
        start: GATSBY_NEW_BRIGHT,
        stop: GATSBY_NEW_BRIGHT,
      },
    },
    BLUE_INVERTED: {
      ...sharedInverted,
      hover: blue[10],
      hoverDark: blue[70],
      muted: BLUE_100,
      primary: blue[50],
      accent: blue[40],
      gradient: {
        start: blue[50],
        stop: blue[50],
      },
    },
    MAGENTA_INVERTED: {
      ...sharedInverted,
      hover: magenta[10],
      hoverDark: magenta[70],
      muted: MAGENTA_100,
      primary: magenta[50],
      accent: magenta[40],
      gradient: {
        start: magenta[50],
        stop: magenta[50],
      },
    },
    GREEN_INVERTED: {
      ...sharedInverted,
      hover: green[20],
      hoverDark: green[70],
      muted: GREEN_100,
      primary: green[60],
      accent: green[50],
      gradient: {
        start: green[50],
        stop: green[50],
      },
    },
    NEUTRAL_INVERTED: {
      ...sharedInverted,
      background: "#0A090B",
      hover: GATSBY_NEW_BRIGHTER,
      hoverDark: GATSBY_NEW_70,
      muted: blackFade[60],
      primary: GATSBY_NEW,
      accent: GATSBY_NEW_BRIGHT,
      gradient: {
        start: GATSBY_NEW_BRIGHT,
        stop: GATSBY_NEW_BRIGHT,
      },
    },
  }
}
